import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileUploadLimits, type FileUploadFeatureKey } from '../../../../hooks/useFileUploadLimits';

interface FileDropZoneProps {
  onFilesSelected: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  compact?: boolean;
  className?: string;
  /** Restrict to these MIME types. Defaults to PDF/JPG/PNG. */
  allowedMimeTypes?: string[];
  /** Which feature's size limit to enforce. Defaults to userDocumentMaxSizeMB. */
  sizeFeatureKey?: FileUploadFeatureKey;
  /** Custom label for the "wrong type" error. */
  typeErrorLabel?: string;
  /** Placeholder shown below the icon in the full (non-compact) layout. */
  placeholderHint?: string;
  /** Optional data-testid for the hidden file input — useful for E2E tests. */
  inputTestId?: string;
}

const DEFAULT_ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

const FileDropZone: React.FC<FileDropZoneProps> = ({
  onFilesSelected,
  accept = '.pdf,.jpg,.jpeg,.png',
  multiple = false,
  compact = false,
  className = '',
  allowedMimeTypes,
  sizeFeatureKey = 'userDocumentMaxSizeMB',
  typeErrorLabel,
  placeholderHint,
  inputTestId,
}) => {
  const { t } = useTranslation();
  const { validateFileSize } = useFileUploadLimits();
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFiles = useCallback((files: File[]): File[] => {
    const valid: File[] = [];
    const allowed = allowedMimeTypes ?? DEFAULT_ALLOWED_TYPES;
    for (const file of files) {
      if (!allowed.includes(file.type)) {
        setError(typeErrorLabel ?? t('Only PDF, JPG, and PNG files are allowed'));
        continue;
      }
      const { valid: sizeOk, maxMB } = validateFileSize(file, sizeFeatureKey);
      if (!sizeOk) {
        setError(t('fileSizeExceedsLimit', { name: file.name, maxMB }));
        continue;
      }
      valid.push(file);
    }
    return valid;
  }, [t, validateFileSize, allowedMimeTypes, sizeFeatureKey, typeErrorLabel]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setError(null);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = validateFiles(multiple ? files : files.slice(0, 1));
    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }
  }, [multiple, onFilesSelected, validateFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = Array.from(e.target.files || []);
    const validFiles = validateFiles(files);
    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }
    // Reset input so same file can be re-selected
    e.target.value = '';
  }, [onFilesSelected, validateFiles]);

  if (compact) {
    return (
      <div className={className}>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          {t('Add More Files')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={accept}
          multiple={multiple}
          onChange={handleFileInput}
          data-testid={inputTestId}
        />
        {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t('Drop files here or click to browse')}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
      className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-all ${
        isDragging
          ? 'border-primary bg-primary/5 scale-[1.01]'
          : 'border-border hover:border-primary/50 hover:bg-muted/30'
      } focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${className}`}
    >
      <svg className="mx-auto h-10 w-10 text-muted-foreground/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
      </svg>
      <p className="mt-2 text-sm font-medium text-foreground">{t('Drop files here or click to browse')}</p>
      <p className="mt-1 text-xs text-muted-foreground">{placeholderHint ?? t('PDF, JPG, PNG')}</p>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={accept}
        multiple={multiple}
        onChange={handleFileInput}
        data-testid={inputTestId}
      />
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default FileDropZone;
